import { TenantSyncStateEnum } from '@reach/shared-types/tenant-filter';
import { useId } from 'react';
import { CategoryPillList } from '../../feature-setup/automation-categories/CategoryPillList/CategoryPillList';
import {
  EngageLibraryRowModel,
  EngageLibrarySyncActionEnum,
  MembershipLockReasonEnum,
} from './engage-library-types';
import { formatLastModified, isMembershipChecked, membershipLockReason } from './engage-library-utils';
import { TenantSyncStatusPill } from './TenantSyncStatusPill';
import styles from './EngageLibraryTable.module.css';

const LOCK_REASON_LABEL: Record<MembershipLockReasonEnum, string> = {
  [MembershipLockReasonEnum.REQUIRED_SEGMENT]: 'Required for every tenant',
  [MembershipLockReasonEnum.SYNCING]: "Can't change while syncing",
};

const PENDING_ACTION_LABEL: Record<EngageLibrarySyncActionEnum, string> = {
  [EngageLibrarySyncActionEnum.SYNC]: 'Will add',
  [EngageLibrarySyncActionEnum.UNSYNC]: 'Will remove',
};

interface EngageLibraryRowProps {
  row: EngageLibraryRowModel;
  pendingAction: EngageLibrarySyncActionEnum | undefined;
  isSaving: boolean;
  isPending: boolean;
  onToggle: (row: EngageLibraryRowModel) => void;
  onRetry: (row: EngageLibraryRowModel) => void;
}

export function EngageLibraryRow({
  row,
  pendingAction,
  isSaving,
  isPending,
  onToggle,
  onRetry,
}: EngageLibraryRowProps) {
  const lockReasonId = useId();
  const lockReason = membershipLockReason(row);
  const lockReasonLabel = lockReason && LOCK_REASON_LABEL[lockReason];
  const isChecked = isMembershipChecked(row, pendingAction);
  const isFailed = row.tenantSyncState === TenantSyncStateEnum.FAILED;
  const showRetry = row.retryable && isFailed && !pendingAction;
  const checkboxLabel = `Sync ${row.name} to this tenant`;

  return (
    <tr className={styles.row} data-pending={Boolean(pendingAction)}>
      <td className={styles.checkboxCell}>
        <input
          type="checkbox"
          checked={isChecked}
          disabled={Boolean(lockReason) || isSaving}
          title={lockReasonLabel}
          onChange={() => onToggle(row)}
          aria-label={checkboxLabel}
          aria-describedby={lockReasonLabel ? lockReasonId : undefined}
        />
      </td>
      <td className={styles.nameCell}>
        <span className={styles.name}>{row.name}</span>
        {row.description && <span className={styles.description}>{row.description}</span>}
        <CategoryPillList categories={row.categories} />
        {lockReasonLabel && (
          <span id={lockReasonId} className={styles.lockReason}>
            {lockReasonLabel}
          </span>
        )}
      </td>
      <td className={styles.statusCell}>
        <TenantSyncStatusPill status={row.tenantSyncState} />
        {pendingAction && (
          <span className={styles.pendingChange} data-action={pendingAction}>
            {PENDING_ACTION_LABEL[pendingAction]}
          </span>
        )}
        {showRetry && (
          <button
            type="button"
            className={styles.retryButton}
            disabled={isPending}
            onClick={() => onRetry(row)}>
            Retry
          </button>
        )}
      </td>
      <td className={styles.modifiedCell}>{formatLastModified(row.updatedAt)}</td>
    </tr>
  );
}
